import { ref } from 'vue'
import type { Ref } from 'vue'

import { useRouter } from 'vue-router'

import { defineStore } from 'pinia'

import { useFirebaseApp } from 'vuefire'

import { Schema, getAI, getGenerativeModel } from 'firebase/ai'

import type { Service } from '#ourahub-intra/types/services'

export type TaskAction = {
  icon: string
  route: string

  action: string
  service: string

  reasoning: string

  dataPrompt: string
  dataSchema: Schema
}

export const useAssistantStore = defineStore('AssistantStore', () => {
  const fbApp = useFirebaseApp()

  const fbAI = getAI(fbApp)

  const router = useRouter()

  const isLoading = ref(false)

  const actions: Ref<TaskAction[] | null> = ref(null)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let dataResult: any = null

  function reset() {
    dataResult = null
    actions.value = null
  }

  async function assist(task: string, items: Service[]) {
    if (!task?.trim()) {
      return
    }

    isLoading.value = true

    const jsonSchema = Schema.object({
      properties: {
        services: Schema.array({
          items: Schema.object({
            properties: {
              id: Schema.string(),
              icon: Schema.string(),
              action: Schema.string(),
              reasoning: Schema.string(),
              route: Schema.string(),
            },
          }),
        }),
      },
    })

    const model = getGenerativeModel(fbAI, {
      //mode: 'prefer_on_device',
      model: 'gemini-2.5-flash',
      systemInstruction:
        'You are an intelligent service selector. You have access to a registry of services and their actions. ' +
        'Your task is to analyze users task and return all even remotely useful or related services and actions in JSON format. ' +
        'Available services and actions: ' +
        JSON.stringify(items),
      generationConfig: {
        responseMimeType: 'application/json',
        responseSchema: jsonSchema,
      },
    })

    const prompt = `User request: "${task}". Select the most appropriate service and action from the registry.`

    const result = await model.generateContent(prompt)
    const response = await result.response

    actions.value = JSON.parse(response.text()).services

    console.log('Assistant actions', actions.value)

    isLoading.value = false
  }

  async function execute(task: string, action: TaskAction) {
    isLoading.value = true

    const model = getGenerativeModel(fbAI, {
      //mode: 'prefer_on_device',
      model: 'gemini-2.5-flash',
      systemInstruction:
        'You are an intelligent data producer. ' +
        'Your task is to analyze the given user task and generate prefilled data for the given action following the given jsonSchema.',
      generationConfig: {
        responseMimeType: 'application/json',
        responseSchema: action.dataSchema,
      },
    })

    const prompt = `Users task: "${task}". Generate the data for "${action.action} following the following instructions:${action.dataPrompt}".`

    const result = await model.generateContent(prompt)
    const response = await result.response

    dataResult = JSON.parse(response.text())

    console.log('Result data', dataResult, action)

    router.push(action.route)

    isLoading.value = false
  }

  function resultData() {
    const data = dataResult

    dataResult = null

    return data
  }

  return { actions, isLoading, reset, assist, execute, resultData }
})
